import { Helmet } from 'react-helmet-async';
import { useMatches, useLocation } from 'react-router';

import { Breadcrumbs as MuiBreadcrumbs } from '@mui/material';

import { paths } from 'src/routes/paths';
import { useReuseName } from 'src/routes/sections/reuse-name';
import BreadcrumbLink from 'src/routes/sections/breadcrumb-link';
import { useInventoryName } from 'src/routes/sections/inventory-name';
import { useSimulationName } from 'src/routes/sections/simulation-name';

import { useResponsive } from 'src/hooks/use-responsive';

import { useTranslate } from 'src/locales';
import { useEntityContext } from 'src/entity/hooks';

import Iconify from 'src/components/iconify';

// ----------------------------------------------------------------------

export default function Breadcrumbs() {
  const { t } = useTranslate();
  const { entity_id, parent_entity_id, myEntities, entity } = useEntityContext();
  const location = useLocation();
  const matches = useMatches();
  const simulation_name = useSimulationName();
  const inventory_name = useInventoryName();
  const reuse_name = useReuseName();
  const smDown = useResponsive('down', 'sm');

  const getText = (txt = '') => {
    switch (txt) {
      case 'parent_entity_name': {
        return entity?.parent ? entity?.parent_name : entity?.name;
      }
      case 'entity_name':
        return entity?.name;
      case 'simulation_name':
        return simulation_name;
      case 'inventory_name':
        return inventory_name;
      case 'reuse_name':
        return reuse_name;
      default:
        return txt;
    }
  };

  const titleParts = [];
  const crumbs = matches
    .filter((match) => Boolean(match.handle?.crumb))
    .map((match, index) => {
      const txt = getText(match.handle.crumb.text);
      titleParts.push(txt || (match.handle.crumb.i18nKey ? t(match.handle.crumb.i18nKey) : ''));
      return (
        <BreadcrumbLink
          key={`crumb${index}`}
          match={match}
          location={location}
          crumb={{
            ...match.handle.crumb,
            text: txt,
          }}
        />
      );
    });

  const title = titleParts.join(' - ');

  if (smDown) {
    crumbs.splice(0, 1);
  }

  const parentEntity = myEntities?.find((_entity) => `${_entity.id}` === `${parent_entity_id}`);
  const homePath = paths.entity.modules.root(
    parentEntity ? parent_entity_id : entity_id,
    parentEntity ? null : parent_entity_id
  );

  return (
    <>
      <Helmet>
        <title> Terranum {title ? ` -  ${title}` : ''}</title>
      </Helmet>

      <MuiBreadcrumbs
        maxItems={smDown ? 2 : 6}
        itemsBeforeCollapse={0}
        itemsAfterCollapse={smDown ? 2 : 4}
        separator="›"
        sx={{
          '& .MuiBreadcrumbs-li': {
            '&>*, &>a>*': {
              color: 'primary.main',
              fontWeight: '800',
              textTransform: 'uppercase',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            },
          },
          '& .MuiBreadcrumbs-separator': {
            color: 'primary.main',
            fontWeight: '800',
          },
        }}
      >
        <BreadcrumbLink
          key="home"
          match={{
            pathname: homePath,
          }}
          location={location}
          crumb={{
            text: <Iconify icon="mdi:home" />,
            to: homePath,
          }}
        />
        {crumbs.map((crumb) => crumb)}
      </MuiBreadcrumbs>
    </>
  );
}

Breadcrumbs.propTypes = {};
